import { z } from "zod";

/**
 * A campaign's steps as a declarative tree (PREC-4258): what an MCP client writes to create or edit
 * a campaign, and what it reads back. `campaignSequenceToGraph` / `campaignGraphToSequence`
 * (campaign-sequence-graph.ts) turn it into the builder's nodes and edges and back, so nobody
 * hand-assembles `list.campaignConfig`.
 *
 * Two choices the builder makes, not this schema:
 * - There is no wait step. A delay is `delayDays` on each step, as the builder's "days"; "wait until
 *   accepted" is `waitUntilMet` on the invite-accepted condition.
 * - Steps never join again after a condition: a condition ends its list and holds its own `yes` and
 *   `no` lists, as the builder's tree does.
 */

/** Days to wait before the step runs, counted from the step before it (the builder's "days"). */
const delayDays = z.number().int().min(0).max(365);

/**
 * LinkedIn senders a step is pinned to, by user id; absent or empty means all of the campaign's
 * senders.
 */
const senderUserIds = z.array(z.number().int().positive()).optional();

/**
 * The stored step this one is: its node id, as `get_campaign` returns it (PREC-4616). Absent for a
 * new step. An edit keeps the node of every step that names it, so the schedule rows pointing at
 * that node (`schedule.stepId`) keep pointing at the same step.
 */
const id = z
	.string()
	.regex(/^n\d{1,9}$/, "A step id is a node id such as n4, as get_campaign returns it")
	.optional();

/** A message body in the builder's HTML; absent or empty means not written yet (generated later). */
const body = z.string().optional();

export const CampaignEmailStepSchema = z.object({
	id,
	type: z.literal("email"),
	delayDays,
	/** The first email of a branch needs one; later emails reply in the same thread without one. */
	subject: z.string().optional(),
	body,
	/** Event-based campaigns: the text used once the event has passed. */
	alternateSubject: z.string().optional(),
	alternateBody: z.string().optional(),
});

export const CampaignLinkedinInviteStepSchema = z.object({
	id,
	type: z.literal("linkedinInvite"),
	delayDays,
	/** The invitation note; absent sends the invite without one. */
	note: z.string().optional(),
	/** The longer note a premium account may send. */
	premiumNote: z.string().optional(),
	alternateNote: z.string().optional(),
	alternatePremiumNote: z.string().optional(),
	senderUserIds,
	/**
	 * Stop the campaign for a lead the sender is already connected to (PREC-4426): no invite, the
	 * lead ends in Outbox → Error. A new step leaves it out to get the default, on; a kept step
	 * leaves it out to keep its stored value. Off skips the invite and runs the next step.
	 */
	stopIfAlreadyConnected: z.boolean().optional(),
});

export const CampaignLinkedinMessageStepSchema = z.object({
	id,
	type: z.literal("linkedinMessage"),
	delayDays,
	body,
	alternateBody: z.string().optional(),
	senderUserIds,
});

export const CampaignInmailStepSchema = z.object({
	id,
	type: z.literal("inmail"),
	delayDays,
	subject: z.string().optional(),
	body,
	alternateSubject: z.string().optional(),
	alternateBody: z.string().optional(),
	senderUserIds,
});

export const CampaignLinkedinVisitStepSchema = z.object({
	id,
	type: z.literal("linkedinVisit"),
	delayDays,
	senderUserIds,
});

export const CampaignLinkedinWithdrawInviteStepSchema = z.object({
	id,
	type: z.literal("linkedinWithdrawInvite"),
	delayDays,
	senderUserIds,
});

/** What a condition checks, by the names the builder's condition picker offers. */
export const CAMPAIGN_CONDITION_CHECKS = [
	"hasEmail",
	"inviteAccepted",
	"isConnected",
	"hasLinkedin",
	"emailOpened",
	"emailClicked",
	"leadPriority",
] as const;

export type CampaignConditionCheck = (typeof CAMPAIGN_CONDITION_CHECKS)[number];

export type CampaignSequenceSendStep =
	| z.infer<typeof CampaignEmailStepSchema>
	| z.infer<typeof CampaignLinkedinInviteStepSchema>
	| z.infer<typeof CampaignLinkedinMessageStepSchema>
	| z.infer<typeof CampaignInmailStepSchema>
	| z.infer<typeof CampaignLinkedinVisitStepSchema>
	| z.infer<typeof CampaignLinkedinWithdrawInviteStepSchema>;

export interface CampaignConditionStep {
	/** The stored step this one is, as for a send step. */
	id?: string;
	type: "condition";
	check: CampaignConditionCheck;
	delayDays: number;
	/** Invite-accepted only: keep checking until the lead accepts, then take the yes branch. */
	waitUntilMet?: boolean;
	/** leadPriority only: the priorities that take the yes branch, e.g. ["P1", "P2"]. */
	priorities?: string[];
	yes: CampaignSequenceStep[];
	no: CampaignSequenceStep[];
}

export type CampaignSequenceStep = CampaignSequenceSendStep | CampaignConditionStep;

const sendStepSchema = z.discriminatedUnion("type", [
	CampaignEmailStepSchema,
	CampaignLinkedinInviteStepSchema,
	CampaignLinkedinMessageStepSchema,
	CampaignInmailStepSchema,
	CampaignLinkedinVisitStepSchema,
	CampaignLinkedinWithdrawInviteStepSchema,
]);

export const CampaignConditionStepSchema: z.ZodType<CampaignConditionStep> = z.lazy(() =>
	z.object({
		id,
		type: z.literal("condition"),
		check: z.enum(CAMPAIGN_CONDITION_CHECKS),
		delayDays,
		waitUntilMet: z.boolean().optional(),
		priorities: z.array(z.string().min(1)).min(1).optional(),
		yes: CampaignStepListSchema,
		no: CampaignStepListSchema,
	})
);

/** One branch's steps in order; a condition can only be the last of them (the tree never rejoins). */
export const CampaignStepListSchema: z.ZodType<CampaignSequenceStep[]> = z.lazy(() =>
	z
		.array(z.union([sendStepSchema, CampaignConditionStepSchema]))
		.refine(
			(steps) => steps.every((step, index) => step.type !== "condition" || index === steps.length - 1),
			{ message: "A condition must be the last step of its list; put what follows in its yes or no list" }
		)
);

/** A whole campaign sequence: at least one step after the start. */
export const CampaignSequenceSchema = z.object({
	steps: CampaignStepListSchema.refine((steps) => steps.length > 0, {
		message: "A campaign needs at least one step",
	}),
});

export type CampaignSequence = z.infer<typeof CampaignSequenceSchema>;
